import { makeObservable, observable, computed, action } from 'mobx';
import { InputBaseModel } from '../models';
import { InputChangeType, InputChangeRecord } from '../types';
import { InputChangeTracker } from '.';

type ChangeCounts = Record<InputChangeType, { count: number; inputIds: string[] }>;

type AggregatedChanges = {
    counts: ChangeCounts;
    changeRecords: InputChangeRecord[];
    totalChangeCount: number;
};

export default class InputGroupChangeTracker {
    inputChangeTrackers: Map<string, InputChangeTracker> = new Map();

    constructor(readonly groupName: string) {
        makeObservable(this, {
            inputChangeTrackers: observable,
            createInputChangeTracker: action,
            clearInputChangeTrackers: action,
            aggregatedChanges: computed,
            changeCounts: computed,
            changedInputIds: computed,
            totalChangeCount: computed
        });
    }

    get aggregatedChanges(): AggregatedChanges {
        return Array.from(this.inputChangeTrackers.values()).reduce<AggregatedChanges>(
            (aggregatedChanges, inputChangeTracker) => {
                if (!inputChangeTracker.changeType) {
                    return aggregatedChanges;
                }

                aggregatedChanges.counts[inputChangeTracker.changeType].count++;

                aggregatedChanges.counts[inputChangeTracker.changeType].inputIds.push(inputChangeTracker.inputId);

                aggregatedChanges.changeRecords.push({
                    inputId: inputChangeTracker.inputId,
                    inputName: inputChangeTracker.inputName,
                    newInput: inputChangeTracker.newInput,
                    previousInput: inputChangeTracker.previousInput,
                    changeType: inputChangeTracker.changeType
                });

                aggregatedChanges.totalChangeCount++;

                return aggregatedChanges;
            },
            {
                counts: {
                    added: { count: 0, inputIds: [] },
                    removed: { count: 0, inputIds: [] },
                    modified: { count: 0, inputIds: [] },
                    unsaved: { count: 0, inputIds: [] }
                },
                changeRecords: [],
                totalChangeCount: 0
            }
        );
    }

    get changeCounts(): ChangeCounts {
        return this.aggregatedChanges.counts;
    }

    get changedInputIds(): string[] {
        return this.aggregatedChanges.changeRecords.map(changeRecord => changeRecord.inputId);
    }

    get totalChangeCount(): number {
        return this.aggregatedChanges.totalChangeCount;
    }

    createInputChangeTracker(
        inputId: string,
        newInput?: InputBaseModel,
        previousInput?: InputBaseModel
    ): InputChangeTracker {
        const inputChangeTracker = new InputChangeTracker(inputId, newInput, previousInput);
        this.inputChangeTrackers.set(inputId, inputChangeTracker);
        return inputChangeTracker;
    }

    getInputChangeTracker(inputId: string): InputChangeTracker | undefined {
        return this.inputChangeTrackers.get(inputId);
    }

    clearInputChangeTrackers() {
        this.inputChangeTrackers.forEach(tracker => tracker.dispose());
        this.inputChangeTrackers.clear();
    }
}
